import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
    SmilesDeepLink,
    SmilesRedemption,
    SmilesRedemptionNotice,
    SmilesVerificationStatus
} from '@/src/types/smiles'
import { clearSmilesSession, readSmilesSession, writeSmilesSession } from '@/src/utils/smiles/smilesSession'

import { RootState } from '../rootReducer'

export interface SmilesState {
    // authToken is never persisted; only account number and status go to the short-TTL cookie.
    verificationStatus: SmilesVerificationStatus
    accountNumber: string
    /** Applied redemption per itineraryId. Client-only — kept here (not in the RTK cache) so it survives itinerary refetches. */
    redemptions: Record<string, SmilesRedemption | null>
    redemptionNotice: SmilesRedemptionNotice
}

const emptyState: SmilesState = {
    verificationStatus: 'IDLE',
    accountNumber: '',
    redemptions: {},
    redemptionNotice: 'NONE'
}

const getSmilesStateFromCookie = (): SmilesState => {
    const session = readSmilesSession()
    if (!session) return emptyState

    return {
        verificationStatus: session.verificationStatus,
        accountNumber: session.accountNumber,
        redemptions: {},
        redemptionNotice: 'NONE'
    }
}

const initialState: SmilesState = typeof window !== 'undefined' ? getSmilesStateFromCookie() : emptyState

export const smilesSlice = createSlice({
    name: 'smiles',
    initialState,
    reducers: {
        setSmilesDeepLink: (state, action: PayloadAction<SmilesDeepLink>) => {
            state.accountNumber = action.payload.accountNumber
            state.verificationStatus = 'PENDING'
            // PENDING is remembered across a reload but does NOT activate attribution — that waits for SUCCESS.
            writeSmilesSession({ accountNumber: state.accountNumber, verificationStatus: 'PENDING' })
        },
        setSmilesVerificationStatus: (state, action: PayloadAction<SmilesVerificationStatus>) => {
            state.verificationStatus = action.payload
            if (action.payload === 'SUCCESS') {
                writeSmilesSession({ accountNumber: state.accountNumber, verificationStatus: 'SUCCESS' })
            } else if (action.payload === 'FAILURE') {
                // Never attribute a failed verification; drop the session entirely.
                clearSmilesSession()
            }
        },
        setSmilesRedemption: (
            state,
            action: PayloadAction<{ itineraryId: string; redemption: SmilesRedemption | null }>
        ) => {
            state.redemptions[action.payload.itineraryId] = action.payload.redemption
        },
        setSmilesRedemptionNotice: (state, action: PayloadAction<SmilesRedemptionNotice>) => {
            state.redemptionNotice = action.payload
        },
        resetSmiles: () => {
            clearSmilesSession()
            return emptyState
        }
    }
})

export const {
    setSmilesDeepLink,
    setSmilesVerificationStatus,
    setSmilesRedemption,
    setSmilesRedemptionNotice,
    resetSmiles
} = smilesSlice.actions

// Attribution is active only for a verified Smiles session, not mere cookie/deep-link presence.
export const selectIsSmilesSession = (state: RootState) => state.smiles.verificationStatus === 'SUCCESS'
export const selectSmilesRedemption = (state: RootState, itineraryId: string): SmilesRedemption | null =>
    state.smiles.redemptions[itineraryId] ?? null
export const selectSmilesRedemptionNotice = (state: RootState) => state.smiles.redemptionNotice

export default smilesSlice.reducer
